import { CommonModule, NgOptimizedImage } from "@angular/common";
import {
    AfterContentInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    ContentChildren,
    Inject,
    Input,
    QueryList,
    signal,
    TemplateRef,
    WritableSignal,
} from "@angular/core";

import { Settings } from "@hermes/app-core";

import { Logger } from "@hermes/logger";
import { LOGGER } from "@hermes/web-logger";

import { ImageVariantsGalleryPartDirective } from "../directives/image-variants-gallery-part.directive";

import { AssetVariantGallery } from "./asset-variant-gallery.interface";

@Component({
    selector: "h-image-variants-gallery",
    templateUrl: "./image-variants-gallery.component.html",
    styleUrls: ["./image-variants-gallery.component.scss"],
    imports: [CommonModule, NgOptimizedImage],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
})
export class ImageVariantsGalleryComponent implements AfterContentInit {
    @Input()
    public assetsParameters: string = "";

    @Input()
    public numberOfImageDisplayed = 3;

    @ContentChildren(ImageVariantsGalleryPartDirective)
    public imageVariantsGalleryParts!: QueryList<ImageVariantsGalleryPartDirective>;

    public accessibilityRef!: TemplateRef<HTMLElement>;

    public assetsRemainingNotDisplayed: WritableSignal<number> = signal(0);
    public assetsSignal: WritableSignal<AssetVariantGallery[]> = signal<
        AssetVariantGallery[]
    >([]);
    public displayedAssets = computed(() =>
        this.assetsSignal().slice(0, this.numberOfImageDisplayed),
    );

    private mappedAssets: AssetVariantGallery[] = [];

    constructor(
        private settings: Settings,
        @Inject(LOGGER) private logger: Logger,
    ) {}

    @Input()
    public set assets(value: AssetVariantGallery[]) {
        if (this.mappedAssets.length === 0) {
            this.mappedAssets = value.map((asset) => ({
                ...asset,
                codeRgb: this.isValidHexColor(asset.codeRgb ?? "")
                    ? asset.codeRgb
                    : "",
            }));
            this.assetsSignal.set(this.mappedAssets);
            this.updateRemainingAssetsDisplay();
        }
    }

    public ngAfterContentInit(): void {
        const partsReference = this.imageVariantsGalleryParts.toArray();

        const accessibility = partsReference.find(
            (directive) =>
                directive.hImageVariantsGalleryPart === "accessibility",
        );

        if (!accessibility) {
            this.logger.warn(
                [
                    "Bad usage of <h-image-variants-gallery> component,",
                    "did you set *hImageVariantsGalleryPart='accessibility'?",
                ].join(" "),
            );
            return;
        }
        this.accessibilityRef = accessibility.templateReference;
    }

    public generateUrlAsset(url: string): string {
        const base = this.settings.assetsUrlProduct;
        const parameters = () =>
            this.assetsParameters.length > 0 ? `&${this.assetsParameters}` : "";
        return url.includes(base) ? url : `${base}${url}${parameters()}`;
    }

    public handleErrorImage(asset: AssetVariantGallery, index: number): void {
        if (asset.isBrokenImage) {
            return;
        }
        this.assetsSignal.update((assets) => {
            const updatedAssets = [...assets];
            updatedAssets[index] = {
                ...asset,
                isBrokenImage: true,
            };
            return updatedAssets;
        });
        this.assetsRemainingNotDisplayed.update((value) => value + 1);
    }

    private isValidHexColor(color: string) {
        const hexColorRegex = /^#?([\dA-Fa-f]{3}|[\dA-Fa-f]{6})$/;
        return hexColorRegex.test(color);
    }

    private updateRemainingAssetsDisplay() {
        const count = this.assetsSignal().length;
        this.assetsRemainingNotDisplayed.set(
            count <= this.numberOfImageDisplayed
                ? 0
                : count - this.numberOfImageDisplayed,
        );
    }
}
